/* eslint-disable arrow-body-style */

import React, { useRef, useMemo, useState, useEffect } from 'react';
import moment from 'moment';
import { useRequest, useMemoizedFn } from 'ahooks';
import TipContext from '@home/components/TipContext';
import CommonContainer from '../components/CommonContainer';
import PerformanceCard from './components/PerformanceCard';
import useDomainPageStore from './_hooks/useDomainPageStore';
import { reportOperationLog } from '@gp/services/global';
import {
  fetchStudioFilterData,
  fetchStudioPerformanceData,
  studioPerformanceDownLoad,
  fetchProjectFilterData,
  fetchProjectPerformanceData,
  projectPerformanceDownLoad,
} from './services';
import { getArraySort } from '@gp/utils/common';
import { getTableRecords, getTableOptions, handleReqParams } from './utils';
import { CONSTANT_TEXTS } from '@gp/constants/const';
import { ALL_REPORT_OPERATION_LOG } from '@gp/constants/operationLogParams';

import type { SortBy } from 'tea-component/lib/table/addons';
import type { TableListOptionsType } from '@gp/components/GeneralTable/index.d';
import type { StudioPerformanceDataType, IStudioPerformanceParams } from './types/services';

import './index.scss';

interface IProjectPerformanceStudioFilterOption {
  value: string;
  text: string;
  projects: {
    id: string;
    name: string;
  }[];
  [keyName: string]: any;
}

const PerformanceOverview: React.FC = () => {
  const {
    studioSelectStudioIdInitial,
    studioYoyInitial,
    studioMomInitial,
    studioOrderInitial,
    studioOrderMetricInitial,

    projectSelectProjectIdInitial,
    projectSelectStudioIdInitial,
    projectYoyInitial,
    projectMomInitial,
    projectOrderInitial,
    projectOrderMetricInitial,
  } = useDomainPageStore({
    getStudioPerformanceParams: () => {
      return studioPerformanceParamsListRef.current;
    },
    getProjectPerformanceParams: () => {
      return projectPerformanceParamsListRef.current;
    },
  });

  // 头部显示的时间
  const [headerDate, setHeaderDate] = useState('');
  // 控制头部时间只取一次
  const isFinishHeaderDate = useRef(false);
  // 通知Project筛选器刷新下拉项及重新全选
  const [projectPerformanceProjectFilterRefreshRenderKey, setProjectPerformanceProjectFilterRefreshRenderKey] =
    useState(0);
  // studio全选时project筛选器的下拉项
  const projectPerformanceAllProjectFilterOptionsRef = useRef<IGPSelectOptionWithGroup[]>([]);
  const [projectPerformanceProjectFilterOptions, setProjectPerformanceProjectFilterOptions] = useState<
  IGPSelectOptionWithGroup[]
  >([]);
  const studioPerformanceParamsListRef = useRef<IStudioPerformanceParams>({
    select_id: studioSelectStudioIdInitial,
    yoy: studioYoyInitial,
    mom: studioMomInitial,
    order: studioOrderInitial,
    order_metric: studioOrderMetricInitial,
  });
  const projectPerformanceParamsListRef = useRef<IStudioPerformanceParams>({
    select_id: projectSelectProjectIdInitial,
    select_id_sub: projectSelectStudioIdInitial,
    yoy: projectYoyInitial,
    mom: projectMomInitial,
    order: projectOrderInitial,
    order_metric: projectOrderMetricInitial,
  });
  const projectPerformanceStudioFilterOptionsRef = useRef<IProjectPerformanceStudioFilterOption[]>([]);

  const { loading: studioPerformanceFilterLoading, data: studioPerformanceFilterData } =
    useRequest(fetchStudioFilterData);

  const { loading: projectPerformanceFilterLoading, data: projectPerformanceFilterData } =
    useRequest(fetchProjectFilterData);

  const {
    loading: studioPerformanceLoading,
    data: studioPerformanceData,
    run: studioPerformanceRunOriginal,
  } = useRequest(fetchStudioPerformanceData, { manual: true });

  const studioPerformanceRun = (params: IStudioPerformanceParams) => {
    const paramsHandled = handleReqParams(params);
    studioPerformanceRunOriginal(paramsHandled);
  };

  const {
    loading: projectPerformanceLoading,
    data: projectPerformanceData,
    run: projectPerformanceRunOriginal,
  } = useRequest(fetchProjectPerformanceData, { manual: true });

  const projectPerformanceRun = (params: IStudioPerformanceParams) => {
    const paramsHandled = handleReqParams(params);
    projectPerformanceRunOriginal(paramsHandled);
  };

  const studioPerformanceStudioFilterOptions = useMemo(() => {
    const studioList = studioPerformanceFilterData?.list;
    if (!studioList || !Array.isArray(studioList)) {
      return [];
    }
    const _options =
      getArraySort(studioList, 'string', 'asc', 'name')?.map((t: { name: string; id: string }) => {
        return {
          value: t?.id || '',
          text: t?.name || '-',
          label: t?.name || '-',
          groupKey: 'separated',
        };
      }) || [];

    return _options;
  }, [studioPerformanceFilterLoading]);

  const projectPerformanceStudioFilterOptions = useMemo(() => {
    const studioList = projectPerformanceFilterData?.list;
    let newProjectOptions: {
      id: string;
      name: string;
    }[] = [];
    if (!studioList || !Array.isArray(studioList)) {
      projectPerformanceStudioFilterOptionsRef.current = [];
      return [];
    }
    const _options =
      getArraySort(studioList, 'string', 'asc', 'name')?.map((t) => {
        const currentItemProjects = t?.projects || [];
        newProjectOptions = [...newProjectOptions, ...currentItemProjects];
        return {
          value: t?.id || '',
          text: t?.name || '-',
          label: t?.name || '-',
          projects: t?.projects,
          groupKey: 'separated',
        };
      }) || [];

    setProjectPerformanceProjectFilterRefreshRenderKey(Date.now());
    const allProjectOptions =
      getArraySort(newProjectOptions, 'string', 'asc', 'name')?.map((t: { name: string; id: string }) => {
        return {
          value: t?.id || '',
          text: t?.name || '-',
          label: t?.name || '-',
          groupKey: 'separated',
        };
      }) || [];
    setProjectPerformanceProjectFilterOptions(allProjectOptions);
    projectPerformanceAllProjectFilterOptionsRef.current = allProjectOptions;

    projectPerformanceStudioFilterOptionsRef.current = _options;
    return _options;
  }, [projectPerformanceFilterLoading]);

  const studioPerformanceFilterOptions = useMemo(() => {
    return [
      {
        type: 'multipleselect',
        fieldName: 'studio',
        value: studioSelectStudioIdInitial,
        option: studioPerformanceStudioFilterOptions,
        className: 'dbg-performance-card-filter-studio-selector',
        label: 'Studio',
        listWidth: 271,
        disabled: studioPerformanceFilterLoading,
        placeholder: 'Please select Studio',
      },
    ];
  }, [studioPerformanceFilterLoading, studioPerformanceStudioFilterOptions, studioSelectStudioIdInitial]);

  const projectPerformanceFilterOptions = useMemo(() => {
    return [
      {
        refreshRenderKey: projectPerformanceProjectFilterRefreshRenderKey,
        type: 'multipleselect',
        fieldName: 'project',
        value: projectSelectProjectIdInitial,
        option: projectPerformanceProjectFilterOptions,
        className: 'dbg-performance-card-filter-project-selector',
        style: { marginRight: 10 },
        label: 'Project',
        disabled: projectPerformanceFilterLoading,
        placeholder: 'Please select Project',
      },
      {
        type: 'multipleselect',
        fieldName: 'studio',
        value: projectSelectStudioIdInitial,
        option: projectPerformanceStudioFilterOptions,
        className: 'dbg-performance-card-filter-studio-selector',
        style: { marginRight: 10 },
        label: 'Studio',
        listWidth: 271,
        disabled: projectPerformanceFilterLoading,
        placeholder: 'Please select Studio',
      },
    ];
  }, [
    projectPerformanceFilterLoading,
    projectPerformanceStudioFilterOptions,
    projectPerformanceProjectFilterOptions,
    projectPerformanceProjectFilterRefreshRenderKey,
    projectSelectProjectIdInitial,
    projectSelectStudioIdInitial,
  ]);

  const studioPerformanceTableOptions: TableListOptionsType = useMemo(() => {
    return getTableOptions(
      'studio',
      studioPerformanceLoading,
      studioPerformanceData as StudioPerformanceDataType,
      studioPerformanceParamsListRef?.current?.yoy,
      studioPerformanceParamsListRef?.current?.mom,
      {
        by: studioOrderMetricInitial,
        order: studioOrderInitial,
      },
    );
  }, [studioPerformanceLoading]);

  const projectPerformanceTableOptions: TableListOptionsType = useMemo(() => {
    return getTableOptions(
      'project',
      projectPerformanceLoading,
      projectPerformanceData as StudioPerformanceDataType,
      projectPerformanceParamsListRef?.current?.yoy,
      projectPerformanceParamsListRef?.current?.mom,
      {
        by: projectOrderMetricInitial,
        order: projectOrderInitial,
      },
    );
  }, [projectPerformanceLoading]);

  const studioPerformanceTableRecords = useMemo(() => {
    return getTableRecords(studioPerformanceLoading, studioPerformanceData as StudioPerformanceDataType);
  }, [studioPerformanceLoading]);

  const projectPerformanceTableRecords = useMemo(() => {
    return getTableRecords(projectPerformanceLoading, projectPerformanceData as StudioPerformanceDataType);
  }, [projectPerformanceLoading]);

  const handleStudioPerformanceTableSort = useMemoizedFn((v: SortBy<any>) => {
    studioPerformanceParamsListRef.current = {
      ...studioPerformanceParamsListRef.current,
      order: v?.order,
      order_metric: v?.by,
    };
    studioPerformanceRun(studioPerformanceParamsListRef.current);
  });

  const handleProjectPerformanceTableSort = useMemoizedFn((v: SortBy<any>) => {
    projectPerformanceParamsListRef.current = {
      ...projectPerformanceParamsListRef.current,
      order: v?.order,
      order_metric: v?.by,
    };
    projectPerformanceRun(projectPerformanceParamsListRef.current);
  });

  const handleStudioPerformanceFilterChange = useMemoizedFn(
    (studio: string[], project: string[], ym: string[], _handleFilterName: string) => {
      studioPerformanceParamsListRef.current = {
        ...studioPerformanceParamsListRef.current,
        select_id: studio,
        yoy: ym?.includes('1'),
        mom: ym?.includes('2'),
      };
      studioPerformanceRun(studioPerformanceParamsListRef.current);
    },
  );

  const handleProjectPerformanceFilterChange = useMemoizedFn(
    (studio: string[], project: string[], ym: string[], handleFilterName: string) => {
      let selectId: string[] = [];
      let newProjectOptions: {
        id: string;
        name: string;
      }[] = [];
      if (handleFilterName === 'project') {
        selectId = project;
        // studio筛选器变化时需要改变project筛选器的筛选项，并且project筛选器要重新全选
      } else if (handleFilterName === 'studio') {
        // 全选
        if (!studio?.length) {
          setProjectPerformanceProjectFilterRefreshRenderKey(Date.now());
          setProjectPerformanceProjectFilterOptions(projectPerformanceAllProjectFilterOptionsRef.current);
        } else {
          for (let i = 0, l = studio?.length; i < l; i++) {
            const t = studio?.[i];
            const projectPerformanceStudioFilterOptions = projectPerformanceStudioFilterOptionsRef.current;
            if (Array.isArray(projectPerformanceStudioFilterOptions)) {
              for (let j = 0, l = projectPerformanceStudioFilterOptions?.length; j < l; j++) {
                const k = projectPerformanceStudioFilterOptions?.[j];
                if (k?.value === t && Array.isArray(k?.projects)) {
                  const currentItemProjects = k?.projects || [];
                  const currentItemProjectIds = currentItemProjects?.map((item) => item?.id) || [];
                  newProjectOptions = [...newProjectOptions, ...currentItemProjects];
                  selectId = [...selectId, ...currentItemProjectIds];
                }
              }
            }
            if (i === l - 1) {
              setProjectPerformanceProjectFilterRefreshRenderKey(Date.now());
              setProjectPerformanceProjectFilterOptions(
                getArraySort(newProjectOptions, 'string', 'asc', 'name')?.map((t: { name: string; id: string }) => {
                  return {
                    value: t?.id || '',
                    text: t?.name || '-',
                    label: t?.name || '-',
                    groupKey: 'separated',
                  };
                }) || [],
              );
            }
          }
        }
      } else {
        selectId = projectPerformanceParamsListRef?.current?.select_id;
      }
      projectPerformanceParamsListRef.current = {
        ...projectPerformanceParamsListRef.current,
        select_id: selectId,
        select_id_sub: studio,
        yoy: ym?.includes('1'),
        mom: ym?.includes('2'),
      };
      projectPerformanceRun(projectPerformanceParamsListRef.current);
    },
  );

  // 初始化请求数据
  useEffect(() => {
    studioPerformanceRun(studioPerformanceParamsListRef.current);
    projectPerformanceRun(projectPerformanceParamsListRef.current);
    // 页面打点
    reportOperationLog(ALL_REPORT_OPERATION_LOG.STUDIO_MONTHLY_REPORT_PERFORMANCE_OVERVIEW);
  }, []);

  // 组织头部显示的时间
  useEffect(() => {
    if (isFinishHeaderDate.current || studioPerformanceLoading || projectPerformanceLoading) return;
    if (!studioPerformanceData && !projectPerformanceData) return;
    const studioPerformanceGrossRevenueDate = studioPerformanceData?.date_time || '';
    const studioPerformanceNetProfitDate = studioPerformanceData?.date_time_net || '';
    const projectPerformanceGrossRevenueDate = projectPerformanceData?.date_time || '';
    const projectPerformanceNetProfitDate = projectPerformanceData?.date_time_net || '';
    const maxDate = moment.max(
      moment(studioPerformanceGrossRevenueDate || '1970-01', 'YYYY-MM'),
      moment(studioPerformanceNetProfitDate || '1970-01', 'YYYY-MM'),
      moment(projectPerformanceGrossRevenueDate || '1970-01', 'YYYY-MM'),
      moment(projectPerformanceNetProfitDate || '1970-01', 'YYYY-MM'),
    );
    const resultDate = maxDate.isValid() ? maxDate.format('YYYY-MM') : '';
    setHeaderDate(resultDate);
    isFinishHeaderDate.current = true;
  }, [studioPerformanceLoading, projectPerformanceLoading]);

  return (
    <CommonContainer title="Performance Overview" subTitle={`Latest Data: ${headerDate || '-'}`} loading={false}>
      <div className="dbg-studio-monthly-report-performance-overview">
        {/* Studio Monthly Performance */}
        <PerformanceCard
          title={
            <div className="dbg-studio-monthly-report-card-title">
              Studio Monthly Performance
              <TipContext
                className="globalpedia-modal-font14 dbg-studio-monthly-report-card-title-info"
                AWidth={555}
                placement="right"
                tipText={
                  <>
                    <p>【Metrics】: Actual (Revenue & Profit) (After Adjustment)</p>
                    <p>{CONSTANT_TEXTS.DATA_SOURCE}: Reporting Package</p>
                    <p>{CONSTANT_TEXTS.UPDATE_FREQUENCY}: Monthly</p>
                    <div className="tea-bubble-line"></div>
                    <p>【Metrics】: Budget (Revenue & Profit)</p>
                    <p>{CONSTANT_TEXTS.DATA_SOURCE}: Aggregated Annual Budget</p>
                    <p>{CONSTANT_TEXTS.UPDATE_FREQUENCY}: Yearly</p>
                  </>
                }
              />
            </div>
          }
          filterLoading={studioPerformanceFilterLoading}
          filterSelectYM={{ yoy: studioYoyInitial, mom: studioMomInitial }}
          filterSelectValues={{ project: [], studio: studioSelectStudioIdInitial }}
          filterOptions={studioPerformanceFilterOptions}
          filterChange={handleStudioPerformanceFilterChange}
          tableClassName="dbg-studio-monthly-report-performance-overview-studio-list dbg-table-sticky"
          tableLoading={studioPerformanceLoading}
          tableOptions={studioPerformanceTableOptions}
          tableRecords={studioPerformanceTableRecords}
          onTableSort={handleStudioPerformanceTableSort}
          uniqueDownloadId="studio_monthly_report_performance_overview_studio_monthly_performance_download"
          downloadParams={handleReqParams(studioPerformanceParamsListRef.current)}
          downloadServices={studioPerformanceDownLoad}
          downloadLogKey={{
            csv: 'STUDIO_MONTHLY_REPORT_PERFORMANCE_OVERVIEW_STUDIO_PERFORMANCE_CSV_DOWNLOAD_SUBMIT',
            excel: 'STUDIO_MONTHLY_REPORT_PERFORMANCE_OVERVIEW_STUDIO_PERFORMANCE_EXCEL_DOWNLOAD_SUBMIT',
          }}
        />
        {/* Project Monthly Performance */}
        <PerformanceCard
          title={
            <div className="dbg-studio-monthly-report-card-title">
              Project Monthly Performance
              <TipContext
                className="globalpedia-modal-font14 dbg-studio-monthly-report-card-title-info"
                AWidth={555}
                placement="top"
                tipText={
                  <>
                    <p>【Metrics】: Actual (Revenue & Profit)</p>
                    <p>{CONSTANT_TEXTS.DATA_SOURCE}: Reporting Package</p>
                    <p>{CONSTANT_TEXTS.UPDATE_FREQUENCY}: Monthly</p>
                    <div className="tea-bubble-line"></div>
                    <p>【Metrics】: Budget (Revenue & Profit)</p>
                    <p>{CONSTANT_TEXTS.DATA_SOURCE}: Aggregated Annual Budget</p>
                    <p>{CONSTANT_TEXTS.UPDATE_FREQUENCY}: Yearly</p>
                  </>
                }
              />
            </div>
          }
          filterLoading={projectPerformanceFilterLoading}
          filterSelectYM={{ yoy: projectYoyInitial, mom: projectMomInitial }}
          filterSelectValues={{ project: projectSelectProjectIdInitial, studio: projectSelectStudioIdInitial }}
          filterOptions={projectPerformanceFilterOptions}
          filterChange={handleProjectPerformanceFilterChange}
          tableClassName="dbg-studio-monthly-report-performance-overview-project-list dbg-table-sticky"
          tableLoading={projectPerformanceLoading}
          tableOptions={projectPerformanceTableOptions}
          tableRecords={projectPerformanceTableRecords}
          onTableSort={handleProjectPerformanceTableSort}
          uniqueDownloadId="studio_monthly_report_performance_overview_project_monthly_performance_download"
          downloadParams={handleReqParams(projectPerformanceParamsListRef.current)}
          downloadServices={projectPerformanceDownLoad}
          downloadLogKey={{
            csv: 'STUDIO_MONTHLY_REPORT_PERFORMANCE_OVERVIEW_PROJECT_PERFORMANCE_CSV_DOWNLOAD_SUBMIT',
            excel: 'STUDIO_MONTHLY_REPORT_PERFORMANCE_OVERVIEW_PROJECT_PERFORMANCE_EXCEL_DOWNLOAD_SUBMIT',
          }}
        />
      </div>
    </CommonContainer>
  );
};

export default React.memo(PerformanceOverview);
